import React, { useState } from 'react';
import { X, Minus, Plus, ShoppingBag, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWoo } from '../context/WooContext';
import { useLang } from '../context/LanguageContext';
import { track } from '../lib/analytics';

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total, count } = useCart();
  const { money, checkout } = useWoo();
  const { t } = useLang();
  const [loading, setLoading] = useState(false);

  const goCheckout = async () => {
    if (!items.length) return;
    track('checkout_started', { total, items: items.map((i) => ({ title: i.title, boxes: i.boxes, qty: i.qty })) });
    setLoading(true);
    try { await checkout(items); } finally { setLoading(false); }
  };

  return (
    <>
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      <aside
        className={`fixed top-0 right-0 z-[95] h-full w-full max-w-md bg-[#080808] border-l border-white/10 shadow-2xl transition-transform duration-400 flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)' }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#ff2d2d]" />
            <h3 className="font-cond text-xl tracking-wide">{t('yourCart')} ({count})</h3>
          </div>
          <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-white/40 gap-3">
              <ShoppingBag className="w-12 h-12" />
              <p className="font-cond text-lg">{t('cartEmpty')}</p>
            </div>
          )}
          {items.map((it) => (
            <div key={it.key} className="flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-xl p-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ background: `rgba(${it.rgb || '255,45,45'},0.15)` }}>
                <Zap className="w-5 h-5" style={{ color: it.color || '#ff2d2d' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-cond text-base tracking-wide truncate">{it.title}</p>
                <p className="text-sm text-white/50">{money(it.price)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => updateQty(it.key, -1)} className="w-7 h-7 rounded-md border border-white/15 flex items-center justify-center hover:bg-white/10"><Minus className="w-3.5 h-3.5" /></button>
                <span className="w-5 text-center text-sm">{it.qty}</span>
                <button onClick={() => updateQty(it.key, 1)} className="w-7 h-7 rounded-md border border-white/15 flex items-center justify-center hover:bg-white/10"><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <button onClick={() => removeItem(it.key)} className="text-white/40 hover:text-[#ff2d2d] transition-colors"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 px-6 py-5 space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-cond text-lg text-white/60">{t('subtotal')}</span>
            <span className="font-display text-2xl">{money(total)}</span>
          </div>
          <button
            disabled={items.length === 0 || loading}
            onClick={goCheckout}
            data-testid="checkout-button"
            className="w-full py-4 rounded-lg font-cond text-lg tracking-wide bg-[#ff2d2d] text-white disabled:opacity-30 hover:brightness-110 transition-all"
          >
            {loading ? '…' : `${t('checkout')} →`}
          </button>
          <p className="text-center text-xs text-white/40">{t('fastDelivery')}</p>
        </div>
      </aside>
    </>
  );
}
